import { useContext, useEffect } from "react";
import { GA_LABEL_ENUMS, triggerGAEvent } from "../../constant/google-analytics/GAEvents";
import { StateContext } from "../../context/globalContext/context";
import { getAnonUUID, getStableId, getVariant } from "./bucketing";
import { BOOK_CTA_KEY, EXPERIMENTS } from "./experiments.config";

// Module-level cache keyed on `${key}:${identity}` so the same identity-key
// pair never re-hashes. When identity flips (UUID → email on login), the
// cache key changes and a fresh variant is computed automatically.
const variantCache = new Map<string, string>();

// Exposure dedupe keyed on `${key}:${identity}` — fires once per identity
// per session. A login flip (UUID → email) re-fires exposure under the new
// identity so each cohort gets a clean denominator. Matches handbook §02.
const exposedKeys = new Set<string>();

// Status gate — the SINGLE source of truth for "is this experiment live?".
// When status !== "running" (paused / completed / draft), the experiment is
// frozen: every user sees Control (variant A), zero ab_experiment_* events
// fire, and the app behaves identically to its pre-experiment state. Normal
// product GA events (downstream of handleBook etc.) continue firing
// untouched — only the AB-specific layer is suppressed.
function isExperimentRunning(key: string): boolean {
  const exp = EXPERIMENTS[key];
  return !!exp && exp.status === "running";
}

function compute(key: string, identity: string): string {
  const experiment = EXPERIMENTS[key];
  if (!experiment) return "A";
  // Frozen state: paused / completed / draft → force Control. Cache lookup
  // skipped intentionally so a status flip is immediately observable
  // without clearing the cache.
  if (experiment.status !== "running") return "A";
  const cacheKey = `${key}:${identity}`;
  let variant = variantCache.get(cacheKey);
  if (!variant) {
    variant = getVariant(identity, experiment);
    variantCache.set(cacheKey, variant);
  }
  return variant;
}

// Internal hook — every surface hook funnels through this so the
// re-resolution logic stays in one place.
function useResolved(key: string): string {
  // Subscribe to StateContext.userInfo so a login/logout transition triggers
  // a re-render of the consuming component, which re-runs this hook with
  // the freshly-updated email. Matches handbook §02: "the moment a user
  // logs in, the hash input switches to email and stickiness becomes
  // permanent across devices."
  const ctx = useContext(StateContext) as any;
  const email: string = ctx?.userInfo?.email || "";

  // Logged-in → email; logged-out → persistent anon UUID.
  const identity = email || getAnonUUID();
  const variant = compute(key, identity);

  // Exposure side effect — runs in useEffect (not render) so it stays
  // within React's rules. Re-fires after a login flip because [identity]
  // changes. Gated on isExperimentRunning so paused/completed experiments
  // emit zero ab_experiment_exposure events.
  useEffect(() => {
    if (!isExperimentRunning(key)) return;
    const expKey = `${key}:${identity}`;
    if (exposedKeys.has(expKey)) return;
    exposedKeys.add(expKey);
    triggerGAEvent(GA_LABEL_ENUMS.ab_experiment_exposure, "", "", "", {
      experiment_id: key,
      variant_id: variant,
    });
  }, [key, identity, variant]);

  return variant;
}

// Surface hooks — same names as seperateExperiment so call sites are
// design-agnostic. allInOne: all three resolve the single BOOK_CTA_KEY.

export function useTripPageBookButtonVariant(): boolean {
  return useResolved(BOOK_CTA_KEY) === "B";
}

export function useMobileNavBookButtonVariant(): boolean {
  return useResolved(BOOK_CTA_KEY) === "B";
}

export function useShopTravelLabelVariant(): string {
  return useResolved(BOOK_CTA_KEY) === "B" ? "Shop Travel" : "Shop Travel";
}

// Conversion: existing Book-click attribution tagged with experiment_id +
// variant_id via the custom_object 5th arg. Fires for BOTH variants so CTR
// numerator/denominator stay comparable. Click handlers run outside React
// render — getStableId() is the synchronous, identity-aware path here.
export function logBookConversion(source: "trip-page" | "mobile-nav" | "sidebar"): void {
  // Gated on status so paused/completed experiments emit zero
  // ab_experiment_conversion events. The sidebar still routes through the
  // normal handleBook() flow regardless — only the AB-specific event is
  // suppressed.
  if (!isExperimentRunning(BOOK_CTA_KEY)) return;
  const experiment = EXPERIMENTS[BOOK_CTA_KEY];
  if (!experiment) return;
  const variant = getVariant(getStableId(), experiment);
  triggerGAEvent(GA_LABEL_ENUMS.ab_experiment_conversion, "", "", "", {
    experiment_id: BOOK_CTA_KEY,
    variant_id: variant,
    source,
  });
}
